import { Button, Card } from '@design-system';
import { ChevronUp } from 'lucide-react';
import { cn } from '@/lib';
import { FundingPolicyType } from '@shared';
import {
  QuotePriceDisplay,
  QuoteProductInfo,
  QuoteROIDisplay,
  useShortTermCarePolicyManagement,
  type ShortTermCareQuoteProjection,
} from '@/features/shop-insurance';

export interface ShortTermCareQuoteCardProps {
  quote: ShortTermCareQuote;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isSelected?: boolean;
  fundingSourceId?: string | null;
  testId?: string;
  /**
   * Projected LTC coverage and ROI. Computed synchronously, so an absent value
   * means the projection FAILED (or there is no client) — the card renders
   * without those figures rather than showing a legacy-approximated number.
   */
  projection?: ShortTermCareQuoteProjection;
}

const currency = (value: string | number) =>
  Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Short-term care quote card.
 *
 * Deliberately has no Create Quote / illustration action. ManhattanLife builds
 * its illustration client-side from a blob URL, so there is nothing for the
 * worker to fetch and it throws by design — offering the button would make
 * every click a guaranteed failure. See
 * packages/illustrations/src/util/manhattanlife/PROTOCOL.md.
 */
export function ShortTermCareQuoteCard({
  quote,
  isExpanded,
  onToggleExpand,
  isSelected = false,
  fundingSourceId = null,
  testId,
  projection,
}: ShortTermCareQuoteCardProps) {
  const { handleAddPolicy, handleRemovePolicy } = useShortTermCarePolicyManagement({
    quote,
    fundingSourceId,
  });

  const discountLabel = quote.tobacco
    ? 'Tobacco'
    : quote.discountKey === 'household'
      ? 'Household discount'
      : 'Standard';
  const hasHomeCareRider = quote.homeDailyBenefitAmount !== null;

  return (
    <Card
      data-testid={testId}
      data-quote-id={quote.shortTermCareQuoteId}
      className={cn(
        'hover:border-primary hover:ring-primary/10 w-full cursor-pointer gap-0 p-0 ring-4 ring-transparent transition-all duration-200',
        {
          'border-primary/50 bg-linear-to-r from-primary/5 to-primary/1 shadow-none': isSelected,
        },
      )}
      onClick={isSelected ? undefined : onToggleExpand}
    >
      <div className="relative">
        <div className="grid grid-cols-[1fr_auto] gap-4 p-4">
          <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
            <div className="flex flex-col gap-2">
              <QuoteProductInfo
                carrierName={quote.carrier}
                productName={quote.productName}
                showLogo={true}
                showRating={false}
              />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-primary-dark text-xs font-medium">Daily Benefit</span>
              <span
                className="text-primary-dark text-lg font-bold"
                data-testid="stc-quote-daily-benefit"
              >
                ${currency(quote.facilityDailyBenefitAmount ?? 0)}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-primary-dark text-xs font-medium">Benefit Period</span>
              <span className="text-primary-dark text-lg font-bold">
                {quote.facilityBenefitPeriodDays} days
              </span>
            </div>

            <QuoteROIDisplay
              projectedCostCoverage={projection?.projectedCostCoverage}
              policyType={FundingPolicyType.manhattanLifeOmniFlexStc}
              displayROI={projection?.formattedROI}
            />

            <QuotePriceDisplay
              price={Number(quote.modalPremium)}
              fundingOption={quote.premiumFrequency}
              label="Premium"
            />
          </div>

          <div className="flex shrink-0 flex-col gap-2 self-start">
            {isSelected ? (
              <Button
                type="button"
                variant="destructive"
                className="bg-destructive/10 text-destructive hover:bg-destructive/15"
                onClick={handleRemovePolicy}
              >
                Remove Product
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleAddPolicy}
              >
                Select Product
              </Button>
            )}
          </div>
        </div>

        <div className="border-border mt-0 border-t">
          <div
            data-testid="stc-quote-expand-button"
            className={cn('flex items-center justify-between p-4', !isSelected && 'cursor-pointer')}
            onClick={clickEvent => {
              clickEvent.stopPropagation();
              onToggleExpand();
            }}
          >
            <span className="text-primary-dark text-sm font-medium">Policy Details</span>
            <ChevronUp
              className={cn('h-4 w-4 transition-transform', !isExpanded && 'rotate-180')}
            />
          </div>

          {isExpanded && (
            <div className="grid grid-cols-1 gap-6 px-4 pb-4 md:grid-cols-2">
              <div className="select-none">
                <h4 className="text-primary-dark mb-2 text-sm font-bold">Benefits</h4>
                <div className="space-y-1">
                  {quote.facilityMaximumBenefitPool && (
                    <p
                      className="text-primary-dark/90 text-sm leading-relaxed"
                      data-testid="stc-quote-detail-max-pool"
                    >
                      <span className="font-medium">Facility Maximum Benefit: </span>$
                      {currency(quote.facilityMaximumBenefitPool)}
                    </p>
                  )}
                  <p
                    className="text-primary-dark/90 text-sm leading-relaxed"
                    data-testid="stc-quote-detail-daily-benefit"
                  >
                    <span className="font-medium">Facility Daily Benefit: </span>$
                    {currency(quote.facilityDailyBenefitAmount ?? 0)}
                  </p>
                  <p className="text-primary-dark/90 text-sm leading-relaxed">
                    <span className="font-medium">Facility Benefit Period: </span>
                    {quote.facilityBenefitPeriodDays} days
                  </p>
                  {/* Two variants of every election are stored, ~40% apart in
                      premium, so the card has to say which one it is. */}
                  <p className="text-primary-dark/90 text-sm leading-relaxed">
                    <span className="font-medium">Inflation Protection: </span>
                    {quote.inflationOption === 'simple5' ? '5% Simple' : 'None'}
                  </p>
                  <p
                    className="text-primary-dark/90 text-sm leading-relaxed"
                    data-testid="stc-quote-detail-elimination-period"
                  >
                    <span className="font-medium">Facility Elimination Period: </span>
                    {quote.facilityEliminationPeriodDays ?? '—'} days
                  </p>
                  {hasHomeCareRider && (
                    <>
                      <p className="text-primary-dark/90 text-sm leading-relaxed">
                        <span className="font-medium">Home Daily Benefit: </span>$
                        {currency(quote.homeDailyBenefitAmount ?? 0)}
                      </p>
                      <p className="text-primary-dark/90 text-sm leading-relaxed">
                        <span className="font-medium">Home Benefit Period: </span>
                        {quote.homeBenefitPeriodDays} days
                      </p>
                      <p className="text-primary-dark/90 text-sm leading-relaxed">
                        <span className="font-medium">Home Elimination Period: </span>
                        {quote.homeEliminationPeriodDays} days
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="select-none">
                <h4 className="text-primary-dark mb-2 text-sm font-bold">Policy</h4>
                <div className="space-y-1">
                  <p className="text-primary-dark/90 text-sm leading-relaxed">
                    <span className="font-medium">Premium: </span>$
                    {currency(quote.modalPremium)} {quote.premiumFrequency}
                  </p>
                  {quote.mandatoryRiderPremium && (
                    <p className="text-primary-dark/90 text-sm leading-relaxed">
                      <span className="font-medium">Included Mandatory Rider: </span>$
                      {currency(quote.mandatoryRiderPremium)}
                    </p>
                  )}
                  <p className="text-primary-dark/90 text-sm leading-relaxed">
                    <span className="font-medium">Rate Class: </span>
                    {discountLabel}
                  </p>
                  <p className="text-primary-dark/90 text-sm leading-relaxed">
                    <span className="font-medium">Issue Age: </span>
                    {quote.issueAge}
                  </p>
                  <p className="text-primary-dark/90 text-sm leading-relaxed">
                    <span className="font-medium">Rates as of: </span>
                    {quote.ratesAsOf}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
